// src/pages/eventRequests/requestDetails/requestDetails.js
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Form, Input, Select, TimePicker } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import useFileViewer from "../../../hooks/useFileViewer";
import Layout from "../../../components/layout/layout";
import "./requestDetails.css";

const { TextArea } = Input;
const { Option } = Select;

const eventFieldMap = {
  child_dedication: [
    { label: "Child's Name", key: "child" },
    { label: "Father's/Guardian's Name", key: "father" },
    { label: "Mother's Name", key: "mother" }
  ],
  baptism: [{ label: "Individual's Name", key: "name" }, null, null],
  engagement: [null, { label: "Groom's Name", key: "groom" }, { label: "Bride's Name", key: "bride" }],
  wedding: [null, { label: "Groom's Name", key: "groom" }, { label: "Bride's Name", key: "bride" }],
  house_dedication: [{ label: "Individual's Name", key: "name" }, null],
  funeral: [{ label: "Individual's Name", key: "name" }, null]
};

function RequestDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state?.item;
  const [form] = Form.useForm();
  const [newRemark, setNewRemark] = useState("");
  const [status, setStatus] = useState(item?.status || "");
  const { handleViewFile, loadingFiles } = useFileViewer(item?.user_id);

  const nameFields = React.useMemo(() => {
    return eventFieldMap[item?.event_type] || [];
  }, [item?.event_type]);

  const viewSupportingDoc = async (fileName, fileKey) => {
    const err = await handleViewFile(fileName, fileKey, "event_request");
    if (err) showNotification(err, "error", "File Viewer");
  };

  const formatToIST = (utcString, type = "full") => {
    if (!utcString) return "";

    const date = new Date(utcString);

    switch (type) {
      case "input-date":
        return date.toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata"
        });
      case "input-time":
        return date.toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour12: false,
          hour: "2-digit",
          minute: "2-digit"
        });
      case "full":
      default:
        return date.toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        });
    }
  };

  const handleUpdate = async () => {
    const updates = {};
    let updateAction = "";

    const newComment = newRemark.trim()
      ? {
          text: newRemark.trim(),
          timestamp: new Date().toISOString()
        }
      : null;

    if (status !== item?.status) {
      updates.status = status;
      updateAction = "updateStatus";
    }

    if (newComment) {
      updates.admin_comment = newComment;
      if (updateAction === "") {
        updateAction = "addComment";
      } else {
        updateAction = "updateAll";
      }
    }

    if (Object.keys(updates).length === 0) {
      showNotification("No changes to update.", "info", "Nothing Changed");
      return;
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        showNotification("Session expired. Please log in again.", "error", "Auth Error");
        return;
      }

      const response = await axios.patch(
        API_ENDPOINTS.EVENT_REQUESTS,
        {
          action: updateAction,
          id: item.id,
          ...updates
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        showNotification("Update successful!", "success", "Updated");
        setNewRemark("");
      } else {
        showNotification(response.data?.message || "Update failed.", "error", "Update Failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      const statusCode = error.response?.status;
      const serverMessage = error.response?.data?.message;

      if (statusCode === 401) {
        showNotification("Session expired. Please log in again.", "error", "Auth Error");
      } else if (statusCode === 403) {
        showNotification("You don't have permission to update this request.", "error", "Access Denied");
      } else if (statusCode === 400 && serverMessage) {
        showNotification(serverMessage, "error", "Validation Error");
      } else {
        showNotification(serverMessage || "Error occurred while updating.", "error", "Update Error");
      }
    }
  };

  const formatEventType = (type) => {
    return type
      ? type
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "";
  };

  React.useEffect(() => {
    if (item) {
      form.setFieldsValue({
        eventType: formatEventType(item.event_type),
        [nameFields[0]?.key]: item?.[nameFields[0]?.key] || "",
        age: item?.age || "",
        [nameFields[1]?.key]: item?.[nameFields[1]?.key] || "",
        [nameFields[2]?.key]: item?.[nameFields[2]?.key] || "",
        phone: item?.phone || "",
        email: item?.email || "",
        eventDate: item?.event_date ? dayjs(formatToIST(item.event_date, "input-date")) : null,
        eventTime: item?.event_date ? dayjs(formatToIST(item.event_date, "input-time"), "HH:mm") : null,
        venue: item?.venue || "",
        notes: item?.notes || "",
        previousComments:
          item?.admin_comments
            ?.map((comment, index) => {
              const time = comment.timestamp ? formatToIST(comment.timestamp) : "Unknown Time";
              const text = comment.text || "";
              return `[${time}] ${text}`;
            })
            .join("\n\n") || "No previous comments"
      });
    }
  }, [form, item, nameFields]);

  return (
    <Layout
      navTitle="Event Requests"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vrq-form-container">
        <div className="vrq-form-wrapper">
          <Form form={form} layout="vertical" className="vrq-antd-form">
            <div className="vrq-form-section">
              <h3 className="vrq-section-title">Event Details</h3>

              <Form.Item label="Type" name="eventType">
                <Input disabled />
              </Form.Item>

              <div className="vrq-form-row">
                {nameFields[0] && (
                  <Form.Item label={nameFields[0].label} name={nameFields[0].key}>
                    <Input disabled />
                  </Form.Item>
                )}
                {["child_dedication", "baptism", "funeral"].includes(item?.event_type) && (
                  <Form.Item label="Age" name="age">
                    <Input disabled type="number" />
                  </Form.Item>
                )}
              </div>

              <div className="vrq-form-row">
                {nameFields[1] && (
                  <Form.Item label={nameFields[1].label} name={nameFields[1].key}>
                    <Input disabled />
                  </Form.Item>
                )}
                {nameFields[2] && (
                  <Form.Item label={nameFields[2].label} name={nameFields[2].key}>
                    <Input disabled />
                  </Form.Item>
                )}
              </div>

              <div className="vrq-form-row">
                <Form.Item label="Phone Number" name="phone">
                  <Input disabled />
                </Form.Item>
                <Form.Item label="Email" name="email">
                  <Input disabled type="email" />
                </Form.Item>
              </div>

              <div className="vrq-form-row">
                <Form.Item label="Date" name="eventDate">
                  <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item label="Time" name="eventTime">
                  <TimePicker disabled style={{ width: "100%" }} format="HH:mm" />
                </Form.Item>
              </div>

              {!["baptism"].includes(item?.event_type) && (
                <Form.Item label="Venue" name="venue">
                  <TextArea rows={3} disabled />
                </Form.Item>
              )}

              {!["house_dedication"].includes(item?.event_type) && (
                <div className="vrq-files-section">
                  <label className="vrq-files-label">Supporting Documents:</label>
                  <div className="vrq-file-list">
                    {Array.isArray(item?.files) && item.files.length > 0 ? (
                      item.files.map((file, index) => {
                        const fileName = file.url?.split("/").pop();
                        return (
                          <div key={index} className="vrq-file-item">
                            <span>
                              📄 {fileName} ({file.size_kb} KB)
                            </span>
                            <Button className="vrq-file-item-btn" onClick={() => viewSupportingDoc(fileName, index)} loading={loadingFiles[index]}>
                              {loadingFiles[index] ? "Loading..." : "View File"}
                            </Button>
                          </div>
                        );
                      })
                    ) : (
                      <div>No documents uploaded.</div>
                    )}
                  </div>
                </div>
              )}

              <Form.Item label="Notes" name="notes">
                <TextArea rows={3} disabled />
              </Form.Item>
            </div>

            <div className="vrq-form-section">
              <h3 className="vrq-section-title">Admin Action</h3>

              <Form.Item label="Status">
                <Select value={status} onChange={(value) => setStatus(value)}>
                  <Option value="">Select</Option>
                  <Option value="To be Reviewed">To be Reviewed</Option>
                  <Option value="In Progress">In-Progress</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Follow Up">Follow-Up</Option>
                  <Option value="Completed">Completed</Option>
                </Select>
              </Form.Item>

              {item?.admin_comments?.length > 0 && (
                <Form.Item label="Previous Comments" name="previousComments">
                  <TextArea rows={6} disabled className="vrq-previous-comments" />
                </Form.Item>
              )}

              <Form.Item label="Add Comment">
                <TextArea rows={3} placeholder="Type your comment here..." value={newRemark} onChange={(e) => setNewRemark(e.target.value)} />
              </Form.Item>
            </div>

            <div className="vrq-form-btn-group">
              <Button className="vrq-form-cancel-btn" onClick={() => navigate("/requestList")}>
                Cancel
              </Button>
              <Button className="vrq-form-message-btn" onClick={handleUpdate}>
                Update
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Layout>
  );
}

export default RequestDetails;
